import React from 'react';
import { DollarSign, TrendingUp, BarChart } from 'lucide-react';

/**
 * Секция финансовых показателей для формы оценки риска банкротства
 */
const FinancialIndicatorsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-orange-500" />
        Финансовые показатели
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Общие активы */}
        <div>
          <label htmlFor="totalAssets" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Общие активы
          </label>
          <input
            type="text"
            id="totalAssets"
            name="totalAssets"
            value={formData.totalAssets}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.totalAssets ? 'input-error' : ''}`}
            placeholder="Сумма всех активов"
          />
          {errors.totalAssets && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.totalAssets}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Валюта баланса (раздел I + раздел II).
          </p>
        </div>

        {/* Общие обязательства */}
        <div>
          <label htmlFor="totalLiabilities" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Общие обязательства
          </label>
          <input
            type="text"
            id="totalLiabilities"
            name="totalLiabilities"
            value={formData.totalLiabilities}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.totalLiabilities ? 'input-error' : ''}`}
            placeholder="Сумма всех обязательств"
          />
          {errors.totalLiabilities && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.totalLiabilities}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Сумма долгосрочных и краткосрочных обязательств (раздел IV + раздел V).
          </p>
        </div>

        {/* Оборотные активы */}
        <div>
          <label htmlFor="currentAssets" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Оборотные активы
          </label>
          <input
            type="text"
            id="currentAssets"
            name="currentAssets"
            value={formData.currentAssets}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentAssets ? 'input-error' : ''}`}
            placeholder="Сумма оборотных активов"
          />
          {errors.currentAssets && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentAssets}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Раздел II актива баланса.
          </p>
        </div>

        {/* Текущие обязательства */}
        <div>
          <label htmlFor="currentLiabilities" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Текущие обязательства
          </label>
          <input
            type="text"
            id="currentLiabilities"
            name="currentLiabilities"
            value={formData.currentLiabilities}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentLiabilities ? 'input-error' : ''}`}
            placeholder="Сумма текущих обязательств"
          />
          {errors.currentLiabilities && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentLiabilities}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Краткосрочные обязательства (раздел V).
          </p>
        </div>

        {/* Рабочий капитал */}
        <div>
          <label htmlFor="workingCapital" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Рабочий капитал
          </label>
          <input
            type="text"
            id="workingCapital"
            name="workingCapital"
            value={formData.workingCapital}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.workingCapital ? 'input-error' : ''}`}
            placeholder="Рабочий капитал"
            disabled
          />
          {errors.workingCapital && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.workingCapital}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Оборотные активы - Текущие обязательства. 
            <span className="italic">(Рассчитывается автоматически)</span>
          </p>
        </div>

        {/* Собственный капитал */}
        <div>
          <label htmlFor="equity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Собственный капитал
          </label>
          <input
            type="text"
            id="equity"
            name="equity"
            value={formData.equity}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.equity ? 'input-error' : ''}`}
            placeholder="Собственный капитал"
          />
          {errors.equity && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.equity}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Раздел III пассива баланса.
          </p>
        </div>

        {/* Нераспределенная прибыль */}
        <div>
          <label htmlFor="retainedEarnings" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Нераспределенная прибыль
          </label>
          <input
            type="text"
            id="retainedEarnings"
            name="retainedEarnings"
            value={formData.retainedEarnings}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.retainedEarnings ? 'input-error' : ''}`}
            placeholder="Нераспределенная прибыль"
          />
          {errors.retainedEarnings && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.retainedEarnings}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Строка 1370 баланса (может быть отрицательной).
          </p>
        </div>

        {/* EBIT (Прибыль до вычета процентов и налогов) */}
        <div>
          <label htmlFor="ebit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            EBIT (прибыль до налогов и процентов)
          </label>
          <input
            type="text"
            id="ebit"
            name="ebit"
            value={formData.ebit}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.ebit ? 'input-error' : ''}`}
            placeholder="EBIT"
          />
          {errors.ebit && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ebit}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Прибыль до уплаты процентов и налогов (строка 2300 + строка 2330).
          </p>
        </div>

        {/* Выручка */}
        <div>
          <label htmlFor="revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Выручка
          </label>
          <input
            type="text"
            id="revenue"
            name="revenue"
            value={formData.revenue}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.revenue ? 'input-error' : ''}`}
            placeholder="Выручка"
          />
          {errors.revenue && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.revenue}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Объем продаж (строка 2110 отчета о финансовых результатах).
          </p>
        </div>
      </div>

      {/* Подсказка по балансу */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-md text-sm text-blue-700 dark:text-blue-300">
        <div className="flex items-start">
          <BarChart className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">Структура баланса:</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              <span className="font-medium">Актив:</span> I. Внеоборотные активы, II. Оборотные активы<br />
              <span className="font-medium">Пассив:</span> III. Капитал и резервы, IV. Долгосрочные обязательства, V. Краткосрочные обязательства
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialIndicatorsSection;